// React imports
import React, { useState, useEffect, useRef } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

// MUI imports
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import { green, grey, blue, orange, red, purple } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import Badge from '@mui/material/Badge';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleMinus, faClock, faCheck, faMagnifyingGlass, faQuestionCircle, faTimesCircle, faTrash, faFileLines, faArrowUpRightFromSquare, faCommentDots, faXmark, faPen, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { faPlane, faShip, faTruck } from '@fortawesome/pro-light-svg-icons';

// RTF Editor
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { MenuButtonBold, MenuButtonItalic, MenuControlsContainer, MenuDivider, MenuSelectHeading, RichTextEditor } from 'mui-tiptap';

// Local imports
import TipTapMentionSuggestion from '../components/TipTapMentionSuggestion.js';
import { PublishedRatesView as CargofivePublishedRatesView } from './RatesImport.cargofive.js';
import { PublishedRatesView as MonadaPublishedRatesView } from './RatesImport.monada.js';
import { renderMentions } from '../../utils/mentions.js';

dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

function ImportLog(props) {
    const { org, me, accesstoken, unlocode, seaPorts } = props;
    const [ cargofiveData, setCargofiveData ] = useState({});
    const [ showImportRates, setShowImportRates ] = useState(null);
    const theme = useTheme();
    const [ ratesImportLog, setRatesImportLog ] = useState(null);
    const [ loadingCharges, setLoadingCharges ] = useState(null);
    const [ showValidOnly, setShowValidOnly ] = useState(true);
    const [ commentDrawer, setCommentDrawer ] = useState(null); // ratesImport item
    const [ deleteConfirm, setDeleteConfirm ] = useState(null); // ratesImport item pending delete
    const [ deleting, setDeleting ] = useState(false);

    function onUpdate({ ratesImport }) {
        setRatesImportLog(ratesImportLog.map(l => l.id === ratesImport.id ? ratesImport : l));
    }

    // Admin-only manual status override (Published / Unpublished). The override map gives
    // instant chip feedback (and is honored first by getStatusChip regardless of the row's
    // cargofive/monada parsing state); the change is also persisted via the admin-gated
    // endpoint so it survives a refresh (the saved l.status is honored on reload).
    const [ statusMenu, setStatusMenu ] = useState(null); // { anchorEl, item }
    const [ statusOverride, setStatusOverride ] = useState({}); // id -> 'published' | 'unpublished'

    function effectiveStatus(l) {
        if (!l) return null;
        if (statusOverride[l.id]) return statusOverride[l.id];
        return l.status === 'published' ? 'published' : 'unpublished';
    }

    async function onChangeStatus({ item, status }) {
        setStatusMenu(null);
        if (effectiveStatus(item) === status) return;
        setStatusOverride(prev => ({ ...prev, [item.id]: status })); // optimistic
        try {
            const { data: updatedImport } = await axios.put(
                `/api/rates/import/${item.id}/status?organizationId=${org.id}&accesstoken=${accesstoken}`,
                { status },
            );
            onUpdate({ ratesImport: updatedImport });
        } catch (e) {
            console.error('[ImportLog] Error persisting status change', e);
            setStatusOverride(prev => { const next = { ...prev }; delete next[item.id]; return next; }); // revert
        }
    }

    useEffect(() => {
        (async () => {
            const { data: _ratesImportLog } = await axios.get(`/api/rates/import?organizationId=${org.id}&accesstoken=${accesstoken}`);
            setRatesImportLog(_ratesImportLog);
        })();
    }, [org.id, accesstoken]);

    // Lazy status fetching - only fetch when rows scroll into view, newest first
    const importMapRef = useRef(new Map());
    const fetchedStatusRef = useRef(new Set());
    const statusQueueRef = useRef([]);
    const processingStatusRef = useRef(false);
    const observerRef = useRef(null);

    if (ratesImportLog) {
        importMapRef.current = new Map(ratesImportLog.map(l => [l.id, l]));
    }

    const processStatusQueueRef = useRef(null);
    processStatusQueueRef.current = async () => {
        if (processingStatusRef.current) return;
        processingStatusRef.current = true;
        while (statusQueueRef.current.length > 0) {
            const batch = statusQueueRef.current.splice(0, 5);
            await Promise.all(batch.map(async (l) => {
                try {
                    const { data } = await axios.get(`/api/cargofive/${l.id}/status?accesstoken=${accesstoken}&organizationId=${org.id}`);
                    setCargofiveData(prev => ({ ...prev, [data.id]: data }));
                } catch (e) {
                    console.error(`Error fetching cargofive status for ${l.id}`, e);
                }
            }));
        }
        processingStatusRef.current = false;
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            let added = false;
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const importId = entry.target.dataset.importId;
                    if (importId && !fetchedStatusRef.current.has(importId)) {
                        const importItem = importMapRef.current.get(importId);
                        if (importItem?.cargofiveData) {
                            fetchedStatusRef.current.add(importId);
                            statusQueueRef.current.push(importItem);
                            added = true;
                        }
                    }
                    observer.unobserve(entry.target);
                }
            });
            if (added) processStatusQueueRef.current();
        });
        observerRef.current = observer;
        return () => observer.disconnect();
    }, []);

    if (!ratesImportLog) {
        return (
            <Box style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', paddingTop: '36px' }}>
                <CircularProgress />
            </Box>
        );
    }

    async function onOpen(ratesImportId) {
        const ratesImportItem = ratesImportLog.find(l => l.id === ratesImportId);
        if (ratesImportItem.cargofiveData) {
            const cargofiveDataItem = cargofiveData[ratesImportItem.cargofiveData.id];
            let monadaRates = null;

            if (cargofiveDataItem.status === 'Published') {
                setLoadingCharges({ open: true, message: 'Loading rates...' });

                // Try pre-computed rates first (instant if cron already converted)
                try {
                    const { data: convertedResult } = await axios.get(`/api/cargofive/${ratesImportItem.id}/converted-rates?accesstoken=${accesstoken}&organizationId=${org.id}`);
                    if (convertedResult.cached) {
                        monadaRates = convertedResult.rates;
                        setLoadingCharges(null);
                        setShowImportRates({ ratesImport: ratesImportItem, rates: monadaRates });
                        return;
                    }
                } catch (e) {
                    console.log('[RatesImport] Pre-computed rates not available, converting server-side');
                }

                // Fall back to server-side conversion
                setLoadingCharges({ open: true, message: 'Converting rates (server-side)...' });
                try {
                    const { data: convertResult } = await axios.post(`/api/cargofive/${ratesImportItem.id}/convert?accesstoken=${accesstoken}&organizationId=${org.id}`);
                    monadaRates = convertResult.rates;
                } catch (e) {
                    console.error('[RatesImport] Server-side conversion failed', e);
                    setLoadingCharges(null);
                    return;
                }
            }

            setLoadingCharges(null);
            setShowImportRates({ ratesImport: ratesImportItem, rates: monadaRates });
        } else {
            setShowImportRates({ ratesImport: ratesImportItem });
        }
    }

    async function onDelete(item) {
        setDeleting(true);
        try {
            // Admin-gated server-side; removes the import from the Tariff Import History.
            await axios.delete(`/api/rates/import/${item.id}?accesstoken=${accesstoken}&organizationId=${org.id}`);
            setRatesImportLog(prev => prev.filter(l => l.id !== item.id));
            setDeleteConfirm(null);
        } catch (e) {
            console.error('[ImportLog] Error deleting import', e);
        } finally {
            setDeleting(false);
        }
    }
    
    function getStatusChip(l) {
        // A manually-set status (in-memory override, or the persisted l.status) wins over the
        // cargofive/parsing-derived chip. Untouched rows (l.status unset) keep the rich logic.
        const manual = statusOverride[l.id]
            || (l.status === 'published' ? 'published' : (l.status === 'unpublished' ? 'unpublished' : null));
        if (manual === 'published') {
            const expired = l.cargofiveData && cargofiveData[l.cargofiveData.id]?.status === 'Expired';
            return <Chip size='small' label={expired ? 'Published & Expired' : 'Published'} icon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: expired ? orange[50] : green[50], color: expired ? orange[800] : green[800], '& .MuiChip-icon': { color: expired ? orange[600] : green[600] } }} />;
        }
        if (manual === 'unpublished') {
            return <Chip size='small' label='Unpublished' icon={<FontAwesomeIcon icon={faCircleMinus} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: grey[100], color: grey[700], '& .MuiChip-icon': { color: grey[500] } }} />;
        }
        if (l.cargofiveData && l.status === 'published') {
            const expired = cargofiveData[l.cargofiveData.id]?.status === 'Expired';
            return <Chip size='small' label={expired ? 'Published & Expired' : 'Published'} icon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: expired ? orange[50] : green[50], color: expired ? orange[800] : green[800], '& .MuiChip-icon': { color: expired ? orange[600] : green[600] } }} />;
        }
        if (l.cargofiveData && l.status !== 'published') {
            if (!cargofiveData[l.cargofiveData.id]) return <CircularProgress size={13} />;
            const status = cargofiveData[l.cargofiveData.id].status;
            const configs = {
                'Pending':              { label: 'Pending',              icon: faClock,          bg: grey[100],    color: grey[700],   iconColor: grey[500] },
                'Processing':           { label: 'Processing',           icon: faClock,          bg: blue[50],     color: blue[800],   iconColor: blue[500] },
                'Published':            { label: 'Parsed',               icon: faCheck,          bg: blue[50],     color: blue[800],   iconColor: blue[500] },
                'Deleted':              { label: 'Deleted',              icon: faTrash,          bg: red[50],      color: red[700],    iconColor: red[400] },
                'Expired':              { label: 'Expired',              icon: faTimesCircle,    bg: orange[50],   color: orange[800], iconColor: orange[500] },
                'Clarification Needed': { label: 'Clarification Needed', icon: faQuestionCircle, bg: purple[50],   color: purple[800], iconColor: purple[400] },
            };
            const cfg = configs[status] || configs['Pending'];
            return <Chip size='small' label={cfg.label} icon={<FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: cfg.bg, color: cfg.color, '& .MuiChip-icon': { color: cfg.iconColor } }} />;
        }
        if (!l.cargofiveData && l.monadaRateCsv && l.status === 'published') {
            return <Chip size='small' label='Published' icon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: green[50], color: green[800], '& .MuiChip-icon': { color: green[600] } }} />;
        }
        if (!l.cargofiveData && l.monadaRateCsv) {
            return <Chip size='small' label='Done parsing' icon={<FontAwesomeIcon icon={faCircleMinus} style={{ fontSize: 10 }} />}
                sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: blue[50], color: blue[800], '& .MuiChip-icon': { color: blue[500] } }} />;
        }
        return <Chip size='small' label='Processing...' icon={<FontAwesomeIcon icon={faClock} style={{ fontSize: 10 }} />}
            sx={{ fontSize: '11px', fontWeight: 500, height: 22, backgroundColor: grey[100], color: grey[700], '& .MuiChip-icon': { color: grey[500] } }} />;
    }

    const sortedLog = _.sortBy(ratesImportLog, l => -1 * l.created).filter(l => {
        if (!showValidOnly) return true;
        const now = dayjs();
        if (l.cargofiveData) {
            const cf = cargofiveData[l.cargofiveData.id];
            if (!cf?.valid_to) return true; // not yet loaded, keep visible
            return dayjs(cf.valid_to).isAfter(now);
        }
        if (l.validUntil) return dayjs(l.validUntil).isAfter(now);
        return true;
    });

    return (
        <>
            <Box sx={{ p: 3, pb: 1 }}>
                <Typography variant='h6'>Tariff Import History</Typography>
                <Typography variant='caption' sx={{ color: '#94a3b8' }}>
                    A record of all tariff files imported into your workspace — including status, source, and processing details. To upload a new file <a href='../rates/import' style={{ color: '#3b82f6', textDecoration: 'none' }}>click here</a>.
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                    <Chip
                        label='Valid only'
                        size='small'
                        onClick={() => setShowValidOnly(!showValidOnly)}
                        sx={{
                            fontSize: '11px', fontWeight: 500, height: 24, cursor: 'pointer',
                            backgroundColor: showValidOnly ? blue[50] : grey[100],
                            color: showValidOnly ? blue[800] : grey[600],
                            border: `1px solid ${showValidOnly ? blue[200] : grey[300]}`,
                        }}
                    />
                </Box>
            </Box>
            {sortedLog.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, color: grey[500] }}>
                    <FontAwesomeIcon icon={faFileLines} style={{ fontSize: 36, marginBottom: 12, color: grey[300] }} />
                    <Typography variant='body2' color='text.secondary'>No rate imports yet</Typography>
                </Box>
            )}

            {sortedLog.length > 0 && (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'border1', borderRadius: '8px', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size='small'>
                            <TableHead>
                                <TableRow sx={{ '& th': { backgroundColor: grey[50], borderBottom: `1px solid ${grey[200]}`, fontSize: '11px', fontWeight: 600, color: grey[600], textTransform: 'uppercase', letterSpacing: '0.5px', py: 1.5 } }}>
                                    <TableCell>Created</TableCell>
                                    <TableCell>By</TableCell>
                                    <TableCell>Mode</TableCell>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Valid Period</TableCell>
                                    <TableCell>File</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align='right' sx={{ width: 80 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLog.map(l => {
                                    let file = l.file.substr(42);
                                    file = decodeURIComponent(file.substr(file.indexOf('.') + 1));
                                    const ext = _.last(decodeURIComponent(l.file).split('.'))?.toUpperCase();
                                    const canOpen = l?.cargofiveData && (cargofiveData[l.cargofiveData.id]?.status === 'Published');
                                    // Admins can delete any import that isn't currently published.
                                    const canDelete = me.isAdmin && effectiveStatus(l) !== 'published';

                                    return (
                                        <TableRow key={l.id} hover
                                            ref={el => {
                                                if (el && l.cargofiveData && !fetchedStatusRef.current.has(l.id)) {
                                                    el.dataset.importId = l.id;
                                                    observerRef.current?.observe(el);
                                                }
                                            }}
                                            sx={{
                                            '&:last-child td': { borderBottom: 0 },
                                            cursor: canOpen ? 'pointer' : 'default',
                                            '& td': { py: 1.5, fontSize: '13px' },
                                        }} onClick={canOpen ? () => onOpen(l.id) : undefined}>
                                            <TableCell>
                                                <Tooltip title={dayjs(l.created).format('MMMM Do YYYY, h:mm A')} placement='top'>
                                                    <span>{dayjs(l.created).fromNow()}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' sx={{ fontSize: '13px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {l.user?.split('@')[0]}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={l.cargofiveData ? 'Sea' : 'Air'} placement='top'>
                                                    <span><FontAwesomeIcon icon={l.cargofiveData ? faShip : faPlane} style={{ fontSize: 14, color: grey[500] }} /></span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                    {!l.cargofiveData && l.supplier}
                                                    {l.cargofiveData && (() => {
                                                        if (!cargofiveData[l.cargofiveData.id]) return <CircularProgress size={13} />;
                                                        return cargofiveData[l.cargofiveData.id].providers
                                                            .map(p => `${p?.name}${p?.delegation_name ? ` (${p?.delegation_name})` : ''}`)
                                                            .join(', ');
                                                    })()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' sx={{ fontSize: '13px', color: grey[600], whiteSpace: 'nowrap' }}>
                                                    {!l.cargofiveData && `${l.validFrom} - ${l.validUntil}`}
                                                    {l.cargofiveData && (() => {
                                                        if (!cargofiveData[l.cargofiveData.id]) return <CircularProgress size={13} />;
                                                        const p = cargofiveData[l.cargofiveData.id];
                                                        return `${p.valid_from} \u2192 ${p.valid_to}`;
                                                    })()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={file} placement='top'>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Chip label={ext} size='small' sx={{ fontSize: '10px', height: 18, fontWeight: 600, backgroundColor: grey[100], color: grey[600] }} />
                                                        <Typography variant='body2' component='a' href={l.file} target='_blank' rel='noopener'
                                                            onClick={e => e.stopPropagation()}
                                                            sx={{ fontSize: '12px', color: blue[600], textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                                                            {file.length > 24 ? `${file.slice(0, 14)}...${file.slice(-8)}` : file}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {me.isAdmin ? (
                                                    <Tooltip title='Change status' arrow>
                                                        <Box
                                                            component='span'
                                                            onClick={e => { e.stopPropagation(); setStatusMenu({ anchorEl: e.currentTarget, item: l }); }}
                                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', borderRadius: '6px', p: '2px', '&:hover': { backgroundColor: grey[100] } }}
                                                        >
                                                            {getStatusChip(l)}
                                                            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 8, color: grey[400] }} />
                                                        </Box>
                                                    </Tooltip>
                                                ) : getStatusChip(l)}
                                            </TableCell>
                                            <TableCell align='right' onClick={e => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <Tooltip title='Comments' placement='top'>
                                                        <IconButton size='small' onClick={() => setCommentDrawer(l)} sx={{ color: grey[500], '&:hover': { color: blue[600] } }}>
                                                            <Badge badgeContent={(l.updates || []).filter(u => !u.deleted).length} color='primary'
                                                                sx={{ '& .MuiBadge-badge': { fontSize: '9px', height: 14, minWidth: 14 } }}>
                                                                <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 13 }} />
                                                            </Badge>
                                                        </IconButton>
                                                    </Tooltip>
                                                    {canOpen && (
                                                        <Tooltip title='View rates' placement='top'>
                                                            <IconButton size='small' onClick={() => onOpen(l.id)} sx={{ color: blue[600] }}>
                                                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && (
                                                        <Tooltip title='Delete import' placement='top'>
                                                            <IconButton size='small' onClick={() => setDeleteConfirm(l)} sx={{ color: red[400], '&:hover': { color: red[600], backgroundColor: red[50] } }}>
                                                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Admin-only status override menu */}
            <Menu
                anchorEl={statusMenu?.anchorEl}
                open={Boolean(statusMenu)}
                onClose={() => setStatusMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {[
                    { value: 'published', label: 'Published' },
                    { value: 'unpublished', label: 'Unpublished' },
                ].map(opt => {
                    const isCurrent = effectiveStatus(statusMenu?.item) === opt.value;
                    return (
                        <MenuItem
                            key={opt.value}
                            selected={isCurrent}
                            onClick={() => onChangeStatus({ item: statusMenu.item, status: opt.value })}
                            sx={{ fontSize: '13px', minWidth: 160 }}
                        >
                            <ListItemIcon sx={{ minWidth: '24px !important' }}>
                                {isCurrent && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11, color: green[600] }} />}
                            </ListItemIcon>
                            {opt.label}
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* Admin delete confirmation */}
            <Dialog open={Boolean(deleteConfirm)} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth='xs' fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: red[50], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 14, color: red[500] }} />
                        </Box>
                        <Typography variant='h6' sx={{ fontSize: '16px', fontWeight: 600 }}>Delete this import?</Typography>
                    </Box>
                    <Typography variant='body2' sx={{ color: grey[600], mb: 3 }}>
                        This removes the file from the Tariff Import History. This action can’t be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={() => setDeleteConfirm(null)} disabled={deleting} sx={{ textTransform: 'none', color: grey[600] }}>
                            Cancel
                        </Button>
                        <Button onClick={() => onDelete(deleteConfirm)} disabled={deleting} variant='contained' color='error'
                            sx={{ textTransform: 'none' }}
                            startIcon={deleting ? <CircularProgress size={14} color='inherit' /> : <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />}>
                            {deleting ? 'Deleting…' : 'Delete'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {showImportRates && showImportRates.ratesImport.cargofiveData && (
                <CargofivePublishedRatesView
                    rates={showImportRates.rates}
                    onClose={() => setShowImportRates(null)}
                    accesstoken={accesstoken}
                    organization={org}
                    mode='rates-import'
                    ratesImport={showImportRates.ratesImport}
                    onUpdateRatesImport={onUpdate}
                    unlocode={unlocode}
                    seaPorts={seaPorts} />
            )}

            {showImportRates && !showImportRates.ratesImport.cargofiveData && (
                <MonadaPublishedRatesView
                    me={me}
                    ratesImport={showImportRates.ratesImport}
                    accesstoken={accesstoken}
                    org={org}
                    handleClose={() => setShowImportRates(null)}
                    onUpdateRatesImport={onUpdate}
                    unlocode={unlocode}
                    seaPorts={seaPorts}
                />
            )}

            {loadingCharges !== null && (
                <Dialog open={true} onClose={() => {}} maxWidth='xs' fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography variant='body2' sx={{ color: grey[600], fontWeight: 500 }}>
                            {loadingCharges.message}
                        </Typography>
                    </DialogContent>
                </Dialog>
            )}

            <Drawer
                anchor='right'
                open={!!commentDrawer}
                onClose={() => setCommentDrawer(null)}
                PaperProps={{ sx: { width: 520, zIndex: 1300 } }}
            >
                {commentDrawer && (
                    <ImportComments
                        ratesImport={commentDrawer}
                        me={me}
                        org={org}
                        accesstoken={accesstoken}
                        onUpdate={(updatedImport) => {
                            setRatesImportLog(ratesImportLog.map(l => l.id === updatedImport.id ? updatedImport : l));
                            setCommentDrawer(updatedImport);
                        }}
                        onClose={() => setCommentDrawer(null)}
                    />
                )}
            </Drawer>

        </>
    )
}

function ImportComments({ ratesImport, me, org, accesstoken, onUpdate, onClose }) {
    const [ showRichText, setShowRichText ] = useState(false);
    const [ posting, setPosting ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ editing, setEditing ] = useState(null);
    const [ deleting, setDeleting ] = useState(null);
    const rteRef = useRef(null);
    const editRteRef = useRef(null);
    const theme = useTheme();

    const updates = ratesImport.updates || [];

    async function saveUpdates(newUpdates) {
        setError(false);
        try {
            const { data } = await axios.put(`/api/rates/import/${ratesImport.id}/updates?accesstoken=${accesstoken}&organizationId=${org.id}`, { updates: newUpdates });
            onUpdate(data);
            return true;
        } catch (e) {
            setError(true);
            return false;
        }
    }

    async function onPost() {
        const html = rteRef.current?.editor?.getHTML();
        if (html && html.length > 10) {
            setPosting(true);
            const newUpdates = [...updates, {
                created: new Date().getTime(),
                createdBy: me?.email,
                html,
            }];
            if (await saveUpdates(newUpdates)) {
                setShowRichText(false);
            }
            setPosting(false);
        }
    }

    async function onEditSave(comment) {
        const html = editRteRef.current?.editor?.getHTML();
        if (html && html.length > 7) {
            await saveUpdates(updates.map(u => u.created === comment.created && u.createdBy === comment.createdBy ? { ...u, html, edited: new Date().getTime() } : u));
        }
        setEditing(null);
    }

    function onDeleteConfirm(comment) {
        saveUpdates(updates.map(u => _.isEqual(u, comment) ? { ...u, deleted: true } : u));
        setDeleting(null);
    }

    let fileName = '';
    try {
        let f = ratesImport.file.substr(42);
        fileName = decodeURIComponent(f.substr(f.indexOf('.') + 1));
    } catch (e) {}

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: theme.palette.background.default }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant='subtitle2'>Comments</Typography>
                    <Typography variant='caption' sx={{ color: grey[500] }}>
                        {fileName.length > 40 ? `${fileName.slice(0, 25)}...${fileName.slice(-12)}` : fileName}
                    </Typography>
                    <Typography variant='caption' sx={{ color: grey[400], display: 'block' }}>
                        Uploaded by {ratesImport.user?.split('@')[0]} on {dayjs(ratesImport.created).format('MMM D, YYYY')}
                    </Typography>
                </Box>
                <IconButton size='small' onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {!showRichText && (
                    <TextField
                        fullWidth
                        size='small'
                        placeholder='Add a comment...'
                        onMouseDown={() => {
                            setShowRichText(true);
                            const func = () => {
                                if (rteRef.current?.editor) {
                                    rteRef.current.editor.chain().focus().run();
                                } else {
                                    setTimeout(func, 10);
                                }
                            };
                            func();
                        }}
                    />
                )}

                {showRichText && (
                    <>
                        <RichTextEditor
                            ref={rteRef}
                            extensions={[
                                StarterKit,
                                Mention.configure({
                                    suggestion: TipTapMentionSuggestion({ emails: org.users.map(u => u.email) }),
                                }),
                            ]}
                            content=''
                            renderControls={() => (
                                <MenuControlsContainer>
                                    <MenuSelectHeading />
                                    <MenuDivider />
                                    <MenuButtonBold />
                                    <MenuButtonItalic />
                                </MenuControlsContainer>
                            )}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button variant='contained' size='small' onClick={posting ? () => {} : onPost} sx={{ flex: 1 }}>
                                {posting ? <CircularProgress size={18} /> : 'Post'}
                            </Button>
                            <IconButton size='small' color='error' onClick={() => setShowRichText(false)} disabled={posting}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Box>
                    </>
                )}

                {error && (
                    <Alert severity='error' sx={{ mt: 1 }}>
                        <Typography variant='caption'>Error saving comment, please try again.</Typography>
                    </Alert>
                )}

                {updates.filter(u => !u.deleted).sort((a, b) => b.created - a.created).map(comment => (
                    <Box key={comment.created} sx={{ mt: 2, p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: '6px', background: theme.palette.panel1 }}>
                        <Box sx={{ display: 'flex', mb: 0.5 }}>
                            <Typography variant='caption' sx={{ flex: 1, color: theme.palette.text.secondary }}>
                                {comment.createdBy} &bull; {dayjs(comment.created).fromNow()}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '2px' }}>
                                <IconButton size='small' onClick={() => setEditing(comment)} sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.text.primary } }}>
                                    <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                                </IconButton>
                                <IconButton size='small' onClick={() => setDeleting(comment)} sx={{ color: theme.palette.text.disabled, '&:hover': { color: red[500] } }}>
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} />
                                </IconButton>
                            </Box>
                        </Box>
                        {editing?.created === comment.created && editing?.createdBy === comment.createdBy ? (
                            <Box>
                                <RichTextEditor
                                    ref={editRteRef}
                                    extensions={[
                                        StarterKit,
                                        Mention.configure({
                                            suggestion: TipTapMentionSuggestion({ emails: org.users.map(u => u.email) }),
                                        }),
                                    ]}
                                    content={comment.html}
                                    renderControls={() => (
                                        <MenuControlsContainer>
                                            <MenuSelectHeading />
                                            <MenuDivider />
                                            <MenuButtonBold />
                                            <MenuButtonItalic />
                                        </MenuControlsContainer>
                                    )}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Button variant='contained' size='small' onClick={() => onEditSave(comment)}>Save</Button>
                                    <Button variant='outlined' size='small' onClick={() => setEditing(null)}>Cancel</Button>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <div dangerouslySetInnerHTML={{ __html: renderMentions({ html: comment.html, me: me?.email, users: org?.users }) }} style={{ fontSize: '13px' }} />
                                {comment.edited && (
                                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                        (edited {dayjs(comment.edited).fromNow()})
                                    </Typography>
                                )}
                            </>
                        )}
                        {_.isEqual(deleting, comment) && (
                            <Alert severity='error' sx={{ mt: 1, alignItems: 'center' }}>
                                <Typography variant='caption'>Delete this comment?</Typography>
                                <Button size='small' variant='contained' sx={{ ml: 1 }} onClick={() => onDeleteConfirm(comment)}>Yes</Button>
                                <Button size='small' variant='contained' color='error' sx={{ ml: 1 }} onClick={() => setDeleting(null)}>No</Button>
                            </Alert>
                        )}
                    </Box>
                ))}

                {updates.filter(u => !u.deleted).length === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, color: grey[400] }}>
                        <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 28, marginBottom: 8 }} />
                        <Typography variant='body2'>No comments yet</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ImportLog;

